import { daSag, radiusDeltaSagEx } from '@/utils/Formulas';
import AdvancedLensForm from '../AdvancedLensForm';
import Segment from '../AdvancedLensFormSegment';
import GenericLensGenerator from './GenericLensGenerator';
import MissingInputError from '@/errors/MissingInputError';

const PropKDIA = 0.92;
const OPTICAL_DIAMETER = 6.00;
const REVERSE_DIAMETER = 7.20;
const BEVEL_WIDTH = 1.00;

export default class NightFlexMyopiaGenerator extends GenericLensGenerator
{
    checkInput(params)
    {
        // Build the list of required parameters
        const required = ['EYE_CORNEA_DIAMETER'];
        const nbMeridians = this.#getNbMeridians(params);
        for(let j = 1; j <= nbMeridians; j++)
        {
            const s =  j === 1 ? '' : `_${j}`;
            required.push(
                `EYE_CORNEA_RADIUS${s}`,
                `EYE_CORNEA_ECCENTRICITY${s}`
            );
        }
        required.push('REFRACTION_SPHERE');
        required.push('LENS_R0_BASE');

        // Track down those that are missing
        const missing = [];
        for(let i = 0; i < required.length; i++)
        {
            const code = required[i];
            const value = params[code] ?? '';
            if(value === '')
            {
                missing.push(code);
            }
        }

        if(missing.length > 0)
        {
            throw new MissingInputError(missing);
        }
    }

    generate(params)
    {
        const form = new AdvancedLensForm();
        form.diameter = Math.round(PropKDIA * params.EYE_CORNEA_DIAMETER * 10) / 10;
        form.geometry = params.EYE_CORNEA_GEOMETRY ?? 'SYMMETRICAL';

        const nbMeridians = this.#getNbMeridians(params);
        for(let j = 1; j <= nbMeridians; j++)
        {
            const s = j === 1 ? '' : `_${j}`;
            const m = ['FLAT', 'STEEP', 'FLAT2', 'STEEP2'][j - 1];

            // ---------------------------------------- Prepare input

            const {
                [`EYE_CORNEA_RADIUS${s}`]:       kRad,
                [`EYE_CORNEA_ECCENTRICITY${s}`]: kEcc,
            } = params;


            // ---------------------------------------- Calculate values

            // Optical Zone
            const optDia = OPTICAL_DIAMETER;
            const r0 = params.LENS_R0_BASE;

            // Reverse Zone
            const revDia = REVERSE_DIAMETER;
            const kRevSag = daSag(kRad, kEcc, 0, revDia);
            const clOptSag = daSag(r0, 0, 0, optDia);
            const revRad = radiusDeltaSagEx(kRevSag - clOptSag, 0, optDia, revDia);

            // Alignment Zone
            const bevelWidth = 1.00;
            const alignDia = form.diameter - BEVEL_WIDTH;


            // ---------------------------------------- Format output

            // Optical Zone
            form.segments.push(new Segment(m, 'OPTICAL', 'SPHERE', {
                diameter: Math.round(optDia * 100) / 100,
                radius:   Math.round(r0 * 100) / 100,
            }));

            // Reverse Zone
            form.segments.push(new Segment(m, 'REVERSE', 'SPHERE', {
                diameter: Math.round(revDia * 100) / 100,
                radius:   Math.round(revRad * 100) / 100,
            }));

            // Alignment Zone
            form.segments.push(new Segment(m, 'ALIGNMENT', 'ASPHERE', {
                diameter:     Math.round(alignDia * 100) / 100,
                radius:       Math.round(kRad * 100) / 100,
                eccentricity: Math.round(kEcc * 100) / 100,
            }));
        }

        return form;
    }

    /**
     * Get the number of meridians for a parameter set.
     *
     * @param {Record<string, any>} params
     * @returns {1|2|4}
     */
    #getNbMeridians(params)
    {
        return {
            SYMMETRICAL:  1,
            ASYMMETRICAL: 2,
            QUADRANT:     4,
        }[params.EYE_CORNEA_GEOMETRY] ?? 1;
    }
}
